/**
 * CreateDataSourceDialog Component (TASK-017)
 *
 * Dialog for creating new data sources with tabs for EXCEL, WEB, WIKI.
 */
import { useState, useCallback, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { dataSourcesApi } from '@/services/api/dataSources';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
// Wiki tab hidden — imports kept for when it's restored
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from '@/components/ui/select';
import { FileUpload } from './FileUpload';
import type { ContentFormat, DataSourceType } from '@/types/dataSource';
import type { OrganizationMode } from '@/types/settings';

interface CreateDataSourceDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /**
   * Current cabinet mode (TASK-270). In 'ecommerce' the dialog shows ONLY the
   * FEED tab (product feed upload/URL); in 'estimator' (default) it shows the
   * Excel/Web tabs as before. Defaults to 'estimator' when omitted.
   */
  mode?: OrganizationMode;
}

type TabValue = 'excel' | 'web' | 'wiki' | 'feed';

interface FormErrors {
  name?: string;
  file?: string;
  url?: string;
  content?: string;
}

export function CreateDataSourceDialog({
  open,
  onClose,
  onSuccess,
  mode = 'estimator',
}: CreateDataSourceDialogProps): JSX.Element | null {
  const isEcommerce = mode === 'ecommerce';
  // Default tab depends on cabinet mode: FEED for e-commerce, Excel otherwise.
  const defaultTab: TabValue = isEcommerce ? 'feed' : 'excel';
  const [activeTab, setActiveTab] = useState<TabValue>(defaultTab);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // FEED file (YML/XML/JSON) — separate from the Excel FileUpload widget.
  const [feedFile, setFeedFile] = useState<File | null>(null);
  const [_fileError, setFileError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [_isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [content, setContent] = useState('');
  const [contentFormat, setContentFormat] = useState<ContentFormat>('MARKDOWN');
  const [tags, setTags] = useState('');
  // Shared across all source types: bypass segmentation, index into RAG only
  const [isInformational, setIsInformational] = useState(false);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset all form state when dialog closes
      setTimeout(() => {
        setActiveTab(defaultTab);
        setSelectedFile(null);
        setFeedFile(null);
        setFileError(null);
        setFormError(null);
        setErrors({});
        setName('');
        setDescription('');
        setUrl('');
        setContent('');
        setContentFormat('MARKDOWN');
        setTags('');
        setIsInformational(false);
        setIsSubmitting(false);
      }, 200);
    } else {
      // When opening, align the active tab with the current cabinet mode.
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]);

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: (args: {
      file: File;
      name?: string;
      description?: string;
      is_informational?: boolean;
      type?: DataSourceType;
    }) =>
      // Only pass `type` when set (FEED) so the Excel path keeps its 4-arg
      // signature — the server defaults an untyped upload to EXCEL.
      args.type
        ? dataSourcesApi.upload(
            args.file,
            args.name,
            args.description,
            args.is_informational,
            args.type
          )
        : dataSourcesApi.upload(
            args.file,
            args.name,
            args.description,
            args.is_informational
          ),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      setFormError(error.message || 'Ошибка загрузки');
      setIsSubmitting(false);
    },
  });

  // Create mutation (for WEB and WIKI)
  const createMutation = useMutation({
    mutationFn: (data: Parameters<typeof dataSourcesApi.create>[0]) => dataSourcesApi.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (error: Error) => {
      setFormError(error.message || 'Ошибка создания');
      setIsSubmitting(false);
    },
  });

  const handleFileSelect = useCallback((file: File | null) => {
    setSelectedFile(file);
    setFileError(null);
    setErrors((prev) => ({ ...prev, file: undefined }));
  }, []);

  const handleFileError = useCallback((error: string) => {
    setFileError(error);
    setSelectedFile(null);
  }, []);

  const handleFeedFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      setFeedFile(file);
      setErrors((prev) => ({ ...prev, file: undefined }));
    },
    []
  );

  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (activeTab === 'excel') {
      if (!selectedFile) {
        newErrors.file = 'Файл обязателен';
      }
    } else if (activeTab === 'feed') {
      // FEED requires EITHER an uploaded file OR a feed URL.
      if (!feedFile && !url.trim()) {
        newErrors.file = 'Загрузите файл фида или укажите ссылку';
      } else if (url.trim()) {
        try {
          new URL(url);
        } catch {
          newErrors.url = 'Некорректный формат URL';
        }
      }
    } else if (activeTab === 'web') {
      if (!name.trim()) {
        newErrors.name = 'Название обязательно';
      }
      if (!url.trim()) {
        newErrors.url = 'URL обязателен';
      } else {
        // Validate URL format
        try {
          new URL(url);
        } catch {
          newErrors.url = 'Некорректный формат URL';
        }
      }
    } else if (activeTab === 'wiki') {
      if (!name.trim()) {
        newErrors.name = 'Название обязательно';
      }
      if (!content.trim()) {
        newErrors.content = 'Содержимое обязательно';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [activeTab, selectedFile, feedFile, name, url, content]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    if (activeTab === 'excel') {
      if (selectedFile) {
        uploadMutation.mutate({
          file: selectedFile,
          name: name || undefined,
          description: description,
          is_informational: isInformational,
        });
      }
    } else if (activeTab === 'feed') {
      // FEED: a file takes precedence; otherwise create from the feed URL.
      if (feedFile) {
        uploadMutation.mutate({
          file: feedFile,
          name: name || undefined,
          description: description,
          is_informational: isInformational,
          type: 'FEED',
        });
      } else if (url.trim()) {
        createMutation.mutate({
          name: name.trim() || url.trim(),
          type: 'FEED',
          url: url.trim(),
          description: description.trim() || undefined,
          is_informational: isInformational,
        });
      }
    } else if (activeTab === 'web') {
      createMutation.mutate({
        name: name.trim(),
        type: 'WEB',
        url: url.trim(),
        description: description.trim() || undefined,
        is_informational: isInformational,
      });
    } else if (activeTab === 'wiki') {
      const parsedTags = tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      createMutation.mutate({
        name: name.trim(),
        type: 'WIKI',
        content: content.trim(),
        content_format: contentFormat,
        description: description.trim() || undefined,
        tags: parsedTags.length > 0 ? parsedTags : undefined,
        is_informational: isInformational,
      });
    }
  }, [
    activeTab,
    validateForm,
    selectedFile,
    feedFile,
    name,
    description,
    url,
    content,
    contentFormat,
    tags,
    isInformational,
    uploadMutation,
    createMutation,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as TabValue);
    setErrors({});
    setFormError(null);
  }, []);

  if (!open) {
    return null;
  }

  const isPending = uploadMutation.isPending || createMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent
        data-testid="create-data-source-dialog"
        className="sm:max-w-[600px] max-h-[90vh] flex flex-col"
        aria-labelledby="dialog-title"
        aria-modal="true"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle id="dialog-title">Добавить источник данных</DialogTitle>
          <DialogDescription>
            Выберите тип источника данных для добавления.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 pb-4">
        <Tabs value={activeTab} onValueChange={handleTabChange}>
          {isEcommerce ? (
            /* E-commerce mode: only the FEED tab is available. */
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger
                data-testid="tab-feed"
                value="feed"
                aria-selected={activeTab === 'feed'}
              >
                Фид товаров
              </TabsTrigger>
            </TabsList>
          ) : (
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger
                data-testid="tab-excel"
                value="excel"
                aria-selected={activeTab === 'excel'}
              >
                Excel файл
              </TabsTrigger>
              <TabsTrigger
                data-testid="tab-web"
                value="web"
                aria-selected={activeTab === 'web'}
              >
                Web URL
              </TabsTrigger>
              {/* Wiki tab temporarily hidden — functionality preserved below
              <TabsTrigger
                data-testid="tab-wiki"
                value="wiki"
                aria-selected={activeTab === 'wiki'}
              >
                Wiki документ
              </TabsTrigger>
              */}
            </TabsList>
          )}

          {/* Excel Form */}
          <TabsContent data-testid="excel-form" value="excel" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="excel-name">Название</Label>
              <Input
                id="excel-name"
                data-testid="name-input"
                placeholder="Введите название (опционально, по умолчанию имя файла)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="excel-description">Описание</Label>
              <Textarea
                id="excel-description"
                data-testid="description-input"
                placeholder="Введите описание (опционально)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Excel файл *</Label>
              <FileUpload
                onFileSelect={handleFileSelect}
                onError={handleFileError}
                disabled={isPending}
                loading={isPending}
              />
              {errors.file && (
                <p className="text-sm text-red-600">{errors.file}</p>
              )}
            </div>
          </TabsContent>

          {/* Feed Form (TASK-270) — e-commerce product feed: file or URL */}
          <TabsContent data-testid="feed-form" value="feed" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="feed-name">Название</Label>
              <Input
                id="feed-name"
                data-testid="name-input"
                placeholder="Введите название (опционально)"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feed-file">Файл фида (YML / XML / JSON)</Label>
              <Input
                id="feed-file"
                data-testid="feed-file-input"
                type="file"
                accept=".yml,.yaml,.xml,.json,application/xml,text/xml,application/json"
                onChange={handleFeedFileChange}
                disabled={isPending}
              />
              {feedFile && (
                <p className="text-xs text-gray-500" data-testid="feed-file-name">
                  {feedFile.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="feed-url">или ссылка на фид</Label>
              <Input
                id="feed-url"
                data-testid="url-input"
                type="url"
                placeholder="https://example.com/feed.yml"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setErrors((prev) => ({ ...prev, url: undefined, file: undefined }));
                }}
                disabled={isPending}
              />
              {errors.url && <p className="text-sm text-red-600">{errors.url}</p>}
            </div>

            {errors.file && <p className="text-sm text-red-600">{errors.file}</p>}

            <div className="space-y-2">
              <Label htmlFor="feed-description">Описание</Label>
              <Textarea
                id="feed-description"
                data-testid="description-input"
                placeholder="Введите описание (опционально)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </TabsContent>

          {/* Web Form */}
          <TabsContent data-testid="web-form" value="web" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="web-name">Название *</Label>
              <Input
                id="web-name"
                data-testid="name-input"
                placeholder="Введите название"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setErrors((prev) => ({ ...prev, name: undefined }));
                }}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="web-url">URL *</Label>
              <Input
                id="web-url"
                data-testid="url-input"
                type="url"
                placeholder="https://example.com"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setErrors((prev) => ({ ...prev, url: undefined }));
                }}
              />
              {errors.url && (
                <p className="text-sm text-red-600">{errors.url}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="web-description">Описание</Label>
              <Textarea
                id="web-description"
                data-testid="description-input"
                placeholder="Введите описание (опционально)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </TabsContent>

          {/* Wiki Form — temporarily hidden, uncomment TabsTrigger above to restore */}
          {/* <TabsContent data-testid="wiki-form" value="wiki" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="wiki-name">Название *</Label>
              <Input
                id="wiki-name"
                data-testid="name-input"
                placeholder="Введите название"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setErrors((prev) => ({ ...prev, name: undefined }));
                }}
              />
              {errors.name && (
                <p className="text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="wiki-content">Содержимое *</Label>
              <Textarea
                id="wiki-content"
                data-testid="content-textarea"
                placeholder="Введите контент в формате Markdown или HTML"
                rows={6}
                value={content}
                onChange={(e) => {
                  setContent(e.target.value);
                  setErrors((prev) => ({ ...prev, content: undefined }));
                }}
              />
              {errors.content && (
                <p className="text-sm text-red-600">{errors.content}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="wiki-format">Формат контента</Label>
              <Select value={contentFormat} onValueChange={(value) => setContentFormat(value as ContentFormat)}>
                <SelectTrigger id="wiki-format" data-testid="content-format-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MARKDOWN">Markdown</SelectItem>
                  <SelectItem value="HTML">HTML</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wiki-tags">Теги</Label>
              <Input
                id="wiki-tags"
                data-testid="tags-input"
                placeholder="тег1, тег2, тег3"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
              />
              <p className="text-xs text-gray-500">
                Список тегов через запятую
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="wiki-description">Описание</Label>
              <Textarea
                id="wiki-description"
                data-testid="description-input"
                placeholder="Введите описание (опционально)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
          </TabsContent> */}
        </Tabs>

        {/* Shared option (all source types): informational / RAG-only document */}
        <div className="mt-4 space-y-1">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="is-informational"
              data-testid="is-informational-checkbox"
              checked={isInformational}
              onCheckedChange={(checked) => setIsInformational(checked === true)}
              disabled={isPending}
            />
            <Label htmlFor="is-informational" className="cursor-pointer">
              Информационный документ (только в базу знаний)
            </Label>
          </div>
          <p className="text-xs text-gray-500 ml-6">
            Минует анализ услуг — только в базу знаний для ответов на вопросы
          </p>
        </div>

        {formError && (
          <div
            data-testid="form-error"
            className="text-sm text-red-600 mt-2 p-2 bg-red-50 rounded"
          >
            {formError}
          </div>
        )}
        </div>

        <DialogFooter className="mt-4 shrink-0 border-t pt-4">
          <Button
            data-testid="cancel-button"
            variant="outline"
            onClick={handleCancel}
            disabled={isPending}
          >
            Отмена
          </Button>
          <Button
            data-testid="submit-button"
            onClick={handleSubmit}
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2
                  data-testid="submit-loading"
                  className="h-4 w-4 mr-2 animate-spin"
                />
                Создание...
              </>
            ) : (
              'Создать'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CreateDataSourceDialog;
